import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Check, Trash, Plus, RotateCcw } from 'lucide-react';
import StatusPill from '../StatusPill';
import { isVisitEditable } from '../../utils/dateUtils';

interface HeaderSectionProps {
    jobDetails: {
        id: string;
        jobSiteName: string;
        subsite: string;
        markAsComp: boolean;
        isDeleted: boolean;
        status: string;
        jobCompletionStatus: {
            status: string;
        };
        createdAt: string;
        projectStartDate: string;
        userId: {
            name: string;
            id: string;
        };
    };
    formatAddress: string;
    latestAction: string;
    jobsiteAccess: {
        deleteJobsite: boolean;
        restoreJobsite: boolean;
    };
    id: string;
    getCompletionPercentage: (status: string) => number;
    handleViewInMap: () => void;
    // handleEditJobsite: () => void;
    handleMarkCompleted: () => void;
    handleDeleteJobsite: () => void;
    handleRestoreJobsite: () => void;
    userRole: string;
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
    jobDetails,
    formatAddress,
    latestAction,
    jobsiteAccess,
    id,
    getCompletionPercentage,
    handleViewInMap,
    handleMarkCompleted,
    handleDeleteJobsite,
    handleRestoreJobsite,
    userRole
}) => {
    const navigate = useNavigate();
    const isEditable = isVisitEditable(jobDetails.createdAt);
    return (
        <div className='bg-white rounded-md shadow mb-4'>
            <div className='p-4 sm:p-6'>
                {/* Header */}
                <div className='flex flex-row justify-between mb-4'>
                    <div>
                        <h1 className='text-[24px] font-[700] mb-1'>{jobDetails.jobSiteName}</h1>
                        <p className='text-gray-600'>{jobDetails.subsite}</p>
                    </div>
                    <div className='flex flex-row gap-3'>
                        {userRole !== 'interRepre' && (
                            <>
                                {/* Create Visit - Only show for active jobsites */}
                                {!jobDetails.markAsComp && jobDetails.status === 'active' && (
                                    <button
                                        className='btn-primary flex items-center justify-center text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px]'
                                        onClick={() => navigate(`/jobsite-management/details/${id}/create-visit`)}
                                        id="create-visit-button"
                                    >
                                        <Plus className="w-4 h-4" />
                                        <span className='hidden sm:inline ml-2'> Create Visit
                                        </span>
                                    </button>
                                )}
                                {/* Delete - Only show for active jobsites */}
                                {jobsiteAccess.deleteJobsite && jobDetails.status === 'active' && (
                                    <button
                                        className='btn-secondary flex items-center justify-center text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px]'
                                        onClick={handleDeleteJobsite}
                                        id="delete-jobsite-button"
                                    >
                                        <Trash className="w-4 h-4" />
                                        <span className='hidden sm:inline ml-2'> Delete Jobsite
                                        </span>
                                    </button>
                                )}
                                {/* Restore - Only show for admins on inactive/completed jobsites */}
                                {jobsiteAccess.restoreJobsite && userRole === 'admin' && (jobDetails.status === 'inactive' || jobDetails.status === 'completed') && (
                                    <button
                                        className='btn-secondary flex items-center justify-center text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px]'
                                        onClick={handleRestoreJobsite}
                                        id="restore-jobsite-button"
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                        <span className='hidden sm:inline ml-2'> Restore Jobsite
                                        </span>
                                    </button>
                                )}
                                {/* Edit Jobsite - Show for active jobsites, disabled after 60 days */}
                                {!jobDetails.markAsComp && jobDetails.status === 'active' && (
                                    <div title={!isEditable ? 'Editing is only available within 60 days of jobsite creation' : undefined}>
                                        <button
                                            className={`btn-secondary flex items-center justify-center text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px] ${!isEditable ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            onClick={() => isEditable && navigate(`/jobsite-management/details/${id}/edit`)}
                                            disabled={!isEditable}
                                            id="edit-jobsite-button"
                                        >
                                            <Pencil className="w-4 h-4" />
                                            <span className='hidden sm:inline ml-2'> Edit Jobsite
                                            </span>
                                        </button>
                                    </div>
                                )}
                                {/* Mark Complete - Only show for active jobsites */}
                                {jobDetails.status === 'active' && (
                                    <button
                                        className={`btn-secondary flex items-center justify-center text-[24px] sm:text-[16px] rounded-full sm:text-body w-fit sm:w-auto sm:rounded-[8px] ${jobDetails.markAsComp ? 'cursor-not-allowed' : ''}`}
                                        onClick={handleMarkCompleted}
                                        disabled={jobDetails.markAsComp}
                                        id="mark-complete-button"
                                    >
                                        <Check className="w-4 h-4" />
                                        <span className='hidden sm:inline ml-2'> {jobDetails.markAsComp ? 'Completed' : 'Mark as Completed'}
                                        </span>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className='space-y-4'>
                    <p className='text-gray-700 text-sm sm:text-base'>
                        <span className='text-[16px] font-[700] text-black'>Address</span>
                        : {formatAddress || 'No address has been added.'}
                        {formatAddress && (
                            <button
                                onClick={handleViewInMap}
                                className='ml-2 text-primary hover:underline'
                                id="view-in-map-button"
                            >
                                View in Map
                            </button>
                        )}
                    </p>

                    <div className='flex flex-col sm:flex-row sm:justify-between gap-6 sm:gap-0'>
                        <div className='w-full sm:w-[30%]'>
                            <div className='flex items-center gap-2'>
                                <span className='text-[16px] font-[700] text-black'>Status</span>
                                <StatusPill status={jobDetails.status} />
                            </div>
                        </div>

                        {/* Submitted Info */}
                        <div className='w-full sm:w-[30%] md:w-[45%] text-left sm:text-right'>
                            <div className='mb-3'>
                                <p className='text-[16px] font-[700] text-black'>
                                    Submitted by <span className='font-medium text-black'>{jobDetails.userId?.name}</span>
                                </p>
                                <p className='text-[16px] font-[700] text-black'>
                                    Project Start Date <span className='font-medium text-black'>
                                        {new Date(jobDetails.projectStartDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: '2-digit',
                                            timeZone: 'UTC'
                                        })}
                                    </span>
                                </p>
                                <p className='text-[16px] font-[700] text-black'>
                                    Submitted on <span className='font-medium text-black'>
                                        {new Date(jobDetails.createdAt).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: '2-digit'
                                        })}
                                    </span>
                                </p>
                            </div>
                            {userRole === 'admin' && (
                                <button
                                    className='text-primary hover:underline text-sm sm:text-base'
                                    onClick={() => navigate(`/jobsite-management/details/${id}/logs`, {
                                        state: {
                                            jobsiteId: jobDetails.id,
                                            userId: jobDetails.userId.id
                                        }
                                    })}
                                    id="jobsite-logs-button"
                                >
                                    Jobsite Logs
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {userRole === 'admin' && (
                    <div className='bg-gray-100 p-3 sm:p-4 rounded-md'>
                        <h2 className='text-[16px] font-[700] text-black'>Latest Action</h2>
                        <p className='text-gray-600 text-sm'>{latestAction || 'No recent actions found'}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HeaderSection; 